import React, { createContext, useContext, useRef, useCallback, ReactNode } from 'react';
import { 
  UserAccountType, 
  UserSubscriptionType 
} from '../types/auth';

/**
 * 全局用户信息接口（仅包含基本字段）
 * 对应 user.entity.ts 中的指定字段
 */
export interface GlobalUser {
  // 基本信息（user.entity.ts 56-69）
  userID: string;
  userName: string;
  userPhoneCountryCode?: string;
  userPhone?: string;
  userEmail?: string;

  // 个人信息（user.entity.ts 74-103）
  userRealName?: string;
  userNickName?: string;
  userAvatar?: string;
  userPosition?: string;
  userRole?: string;
  userCountry?: string;
  userCity?: string;
  userAccountType?: UserAccountType;
  userAccountStatus?: string;

  // 订阅相关字段（user.entity.ts 105-131）
  userSubscriptionBreakdownVersion?: UserSubscriptionType;
  userSubscriptionBreakdownStartDate?: Date | string;
  userSubscriptionBreakdownExpirationDate?: Date | string;
  userSubscriptionBudgetsVersion?: UserSubscriptionType;
  userSubscriptionBudgetsStartDate?: Date | string;
  userSubscriptionBudgetsExpirationDate?: Date | string;

  // 组织相关字段（user.entity.ts 140-142）
  userOrgID?: string;

  // 时间戳（user.entity.ts 225-229）
  createdAt: Date | string;
  updatedAt: Date | string;
}

interface GlobalUsersContextType {
  // 获取单个用户
  getUser: (userID: string) => GlobalUser | undefined;
  
  // 批量获取用户
  getUsers: (userIDs: string[]) => GlobalUser[];
  
  // 批量添加用户（自动排重）
  addUsers: (users: GlobalUser[]) => void;
  
  // 检查用户是否存在
  hasUser: (userID: string) => boolean;
  
  // 获取所有用户
  getAllUsers: () => GlobalUser[];
  
  // 清空所有用户（登出时调用）
  clearAllUsers: () => void;
}

const GlobalUsersContext = createContext<GlobalUsersContextType | undefined>(undefined);

export const useGlobalUsers = () => {
  const context = useContext(GlobalUsersContext);
  if (context === undefined) {
    throw new Error('useGlobalUsers must be used within a GlobalUsersProvider');
  }
  return context;
};

export const GlobalUsersProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // 使用 ref 存储用户信息，避免触发重新渲染
  const usersMapRef = useRef<Map<string, GlobalUser>>(new Map());

  const getUser = useCallback((userID: string): GlobalUser | undefined => {
    return usersMapRef.current.get(userID);
  }, []);

  const getUsers = useCallback((userIDs: string[]): GlobalUser[] => {
    return userIDs
      .map(id => usersMapRef.current.get(id))
      .filter((user): user is GlobalUser => user !== undefined);
  }, []);

  const addUsers = useCallback((users: GlobalUser[]): void => {
    users.forEach(user => {
      if (user.userID) {
        // 自动排重，确保 userID 唯一
        usersMapRef.current.set(user.userID, user);
      }
    });
  }, []);

  const hasUser = useCallback((userID: string): boolean => {
    return usersMapRef.current.has(userID);
  }, []);

  const getAllUsers = useCallback((): GlobalUser[] => {
    return Array.from(usersMapRef.current.values());
  }, []);

  const clearAllUsers = useCallback((): void => {
    usersMapRef.current.clear();
  }, []);

  const value: GlobalUsersContextType = {
    getUser,
    getUsers,
    addUsers,
    hasUser,
    getAllUsers,
    clearAllUsers,
  };

  return (
    <GlobalUsersContext.Provider value={value}>
      {children}
    </GlobalUsersContext.Provider>
  );
};
